import { z } from 'zod';
/**
 * O'lchov birliklari.
 *
 * Shartnoma paketida turadi, chunki birlik uch joyda BIR XIL tushunilishi
 * kerak: backend saqlaydi va tekshiradi, frontend yorliq ko'rsatadi va
 * miqdor maydonini cheklaydi. Ro'yxat ikki marta yozilsa, ular albatta bir
 * kun ajralib ketadi (`apps/web` `apps/api` dan import qila olmaydi).
 */
export const UNITS = [
    'DONA',
    'KG',
    'M',
    'M2',
    'M3',
    'LITR',
    'KOMPLEKT',
    /* Quyidagi uchtasi 1C moddiy hisobotidan keldi: ularsiz haqiqiy
       omborning 12 ta satri joy topolmasdi (`тн`, `пар`, `рул`). */
    'TONNA',
    'JUFT',
    'RULON',
];
export const unitSchema = z.enum(UNITS);
export const isUnit = (value) => UNITS.includes(value);
/** Foydalanuvchiga ko'rinadigan yorliqlar — o'zbekcha, kichik harflar bilan. */
const UNIT_LABELS = {
    DONA: 'dona',
    KG: 'kg',
    M: 'm',
    M2: 'm²',
    M3: 'm³',
    LITR: 'litr',
    KOMPLEKT: 'komplekt',
    TONNA: 'tonna',
    JUFT: 'juft',
    RULON: 'rulon',
};
export const unitLabel = (unit) => UNIT_LABELS[unit];
/**
 * Butun son majburiy bo'lgan birliklar: yarim dona shpal yoki yarim komplekt
 * asbob bo'lmaydi. Kirim satri va talabnoma miqdori shu qoidaga tayanadi.
 */
const INTEGER_ONLY_UNITS = ['DONA', 'KOMPLEKT', 'JUFT', 'RULON'];
export const isFractionalAllowed = (unit) => !INTEGER_ONLY_UNITS.includes(unit);
/**
 * 1C MODDIY HISOBOTIDAGI birlik matnini ichki birlikka o'giradi.
 *
 * Hisobotda birlik ERKIN MATN va u bir xil narsani bir necha xil yozadi:
 * `метр`, `п/м`, `погонный метр` — uchalasi ham metr; `кв.м` va `м2` —
 * bittasi. Haqiqiy faylda 16 xil yozuv uchradi.
 *
 * Xom matn HAM saqlanadi (`unitRaw`): rasmiy qog'ozda `шт (упаковка)`
 * aynan shu ko'rinishda chiqishi kerak. Bu yerdagi o'girma esa boshqa ish
 * uchun — miqdor butun bo'lishi shartmi degan qoida uchun
 * (`isFractionalAllowed`). Ikkalasini bittaga qo'shsak, yo qog'oz buzilardi,
 * yo yarim dona chiroq paydo bo'lardi.
 *
 * Tanilmagan matn uchun `null` qaytadi — TAXMIN QILINMAYDI. Noto'g'ri
 * taxmin qilingan birlik butun partiyaning miqdorini jimgina boshqa
 * ma'noga o'tkazib yuborardi; import esa o'sha satrni ko'rsatib, odamdan
 * so'raydi.
 */
const UNIT_ALIASES = {
    /* dona va uning o'ram ko'rinishlari. `шт (упаковка)`, `дона(кадок)`
       kabi QAVSLI yozuvlar bu yerda YO'Q — ularni `unitFrom1c` dagi
       umumiy qoida hal qiladi. */
    'шт': 'DONA',
    'шт.': 'DONA',
    'штука': 'DONA',
    'флак.': 'DONA',
    'флак': 'DONA',
    'дона': 'DONA',
    'up': 'DONA',
    // og'irlik
    'кг': 'KG',
    'кг.': 'KG',
    'kg': 'KG',
    'килограмм': 'KG',
    'кило': 'KG',
    'тн': 'TONNA',
    'т': 'TONNA',
    'тонна': 'TONNA',
    // uzunlik — uchta yozuv, bitta ma'no
    'метр': 'M',
    'м': 'M',
    'м.': 'M',
    'п/м': 'M',
    'пог.м': 'M',
    'погонный метр': 'M',
    'm': 'M',
    // yuza va hajm
    'кв.м': 'M2',
    'кв.м.': 'M2',
    'м2': 'M2',
    'м.кв': 'M2',
    'м3': 'M3',
    'куб.м': 'M3',
    'м.куб': 'M3',
    // suyuqlik
    'л': 'LITR',
    'литр': 'LITR',
    'litr': 'LITR',
    // to'plam va boshqalar
    'комплект': 'KOMPLEKT',
    'компл': 'KOMPLEKT',
    'компл.': 'KOMPLEKT',
    'к-т': 'KOMPLEKT',
    // o'zbekcha «to'plam» va 1C ning «комплект/набор» qisqartmasi
    'туплам': 'KOMPLEKT',
    'тўплам': 'KOMPLEKT',
    "to'plam": 'KOMPLEKT',
    'to‘plam': 'KOMPLEKT',
    'toplam': 'KOMPLEKT',
    'комп/наб': 'KOMPLEKT',
    'компл/наб': 'KOMPLEKT',
    'набор': 'KOMPLEKT',
    'наб': 'KOMPLEKT',
    'наб.': 'KOMPLEKT',
    'пар': 'JUFT',
    'пара': 'JUFT',
    'juft': 'JUFT',
    'рул': 'RULON',
    'рул.': 'RULON',
    'рулон': 'RULON',
};
const lookup = (key) => UNIT_ALIASES[key] ?? (isUnit(key.toUpperCase()) ? key.toUpperCase() : null);
/**
 * XOM MATNNING KALITI — `дона(кадок)`, `Дона (Кадок)` va `ДОНА(КАДОК)`
 * bitta narsa.
 *
 * Alohida eksport qilingan, chunki kalitni IKKI tomon yasaydi: server uni
 * bazaga yozadi, brauzer esa xuddi shu kalit bo'yicha «bu birlik
 * allaqachon qo'shilganmi?» deb qaraydi. Ikki joyda ikki xil
 * normallashtirish bo'lsa, odam qo'shgan mos keltirish keyin topilmay
 * qolardi.
 */
export const normalizeUnitRaw = (raw) => raw.trim().toLowerCase().replace(/\s+/g, ' ');
export const unitFrom1c = (raw) => {
    const key = normalizeUnitRaw(raw);
    if (key === '')
        return null;
    const direct = lookup(key);
    if (direct !== null)
        return direct;
    /*
     * OXIRIDAGI QAVS — birlik emas, O'RAM haqidagi izoh.
     *
     * 1C da bir xil narsa `шт`, `шт (упаковка)`, `дона(кадок)`,
     * `шт (бутылка)` bo'lib keladi: qavs ichida nima bilan o'ralgani
     * yoziladi, o'lchov esa baribir DONA. Ilgari har bir ko'rinish
     * ro'yxatga qo'lda qo'shilardi va navbatdagi o'ram turi ("банка",
     * "ведро") butun bir hisobotni to'xtatib qo'yardi.
     *
     * Qavs matnning OXIRIDA bo'lgandagina tashlanadi: `кв.м` kabi
     * yozuvlarga tegmasligi uchun.
     *
     * Xom matn yo'qolmaydi — u `unitRaw` da saqlanadi va rasmiy qog'ozda
     * aynan 1C yozganidek chiqadi.
     */
    const bare = key.replace(/\s*\([^()]*\)$/, '').trim();
    return bare === key || bare === '' ? null : lookup(bare);
};
//# sourceMappingURL=unit.js.map